import { serviceHelper } from '../../config/service-helper';

export class PhoneHelper {
  private static _intlValidationRegex =
    /^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/;

  static formatToE164(number: string, areaCode: string): string {
    if (!number || number.match(/[a-z]/i)) return '';

    const areaCodePattern = new RegExp(`^${areaCode}`, 'gm');
    const firstChar0Pattern = /^0/gm;
    const whitespacePattern = /\s/gm;
    const plusPattern = /\+/gm;
    const parsedValue = number
      .replace(firstChar0Pattern, '')
      .replace(plusPattern, '')
      .replace(whitespacePattern, '')
      .replace(areaCodePattern, '');
    return `+${areaCode}${parsedValue}`;
  }

  static validateNumberLength(phoneNumber: string): boolean {
    const phoneNumberLength = phoneNumber.replace(/[^0-9]/g, '').length;
    return phoneNumberLength >= 5 || phoneNumberLength <= 15;
  }

  static validateNumberLengthOnlyForLocalNumbers(phoneNumber: string): boolean {
    const phoneNumberLength = phoneNumber.replace(/[^0-9]/g, '').length;
    return window.Jbhifi?.storeLocation === 'NZ'
      ? phoneNumberLength >= 8 || phoneNumberLength <= 11
      : phoneNumberLength === 9 || phoneNumberLength === 10;
  }

  static validateWithRegex(phoneNumber: string): boolean {
    if (!phoneNumber) return false;
    return this._intlValidationRegex.test(phoneNumber);
  }

  static async validateWithApi({
    phoneNumber,
    countryCode,
    localNumbersOnly = false,
  }: ValidationProps): Promise<PhoneValidationApiResponse> {
    const numberLengthValidation = localNumbersOnly
      ? this.validateNumberLengthOnlyForLocalNumbers(phoneNumber)
      : this.validateNumberLength(phoneNumber);

    if (!numberLengthValidation) {
      return { isValid: false, phoneType: 'UNKNOWN' };
    }

    try {
      const response = await fetch(
        `${serviceHelper.phoneNumberCheckURL}?countryCode=${countryCode}&phoneNumber=${encodeURIComponent(
          phoneNumber
        )}`,
        { headers: { 'content-type': 'application/json' } }
      );
      const validationResult = await response.json();
      return {
        // Api responds in PascalCase, map to camelCase
        isValid: validationResult.IsValid,
        e164Format: validationResult.E164Format,
        nationalFormat: validationResult.NationalFormat,
        phoneType: validationResult.PhoneType,
        error: validationResult.Error,
      };
    } catch (err) {
      // Assume valid if there's a fetch network error.
      return { isValid: true, phoneType: 'MOBILE' };
    }
  }
}

interface ValidationProps {
  phoneNumber: string;
  countryCode?: string;
  localNumbersOnly?: boolean;
}

export interface PhoneValidationApiResponse {
  isValid: boolean;
  e164Format?: string;
  nationalFormat?: string;
  phoneType?: string;
  error?: Record<string, any>;
}
